// @file Common content handling logic for both drop and paste operations
// This composable provides shared functionality used by:
// - useDropToCreatePost.ts (for drag & drop operations)
// - usePasteToCreatePost.ts (for paste operations)
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { isPhotoAlbum } from '@@/bits/post_attachment'
import { isPostColorId } from '@@/bits/post_color'
import { getAttachmentsFromDraft, MAX_PHOTO_ALBUM_ATTACHMENTS } from '@@/bits/post_drafts'
import { setCustomPropertyValue } from '@@/bits/post_properties'
import { WallWishGroupByValue } from '@@/enums'
import { useComposerModalAlertStore } from '@@/pinia/composer_modal_alert'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceGroupPickerStore } from '@@/pinia/surface_group_picker'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { DraftPost, PostAttributes, WallGroupPost } from '@@/types'
import { usePhotoAlbumEditor } from '@@/vuecomposables/usePhotoAlbumEditor'
import { isEmpty } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import { computed, watchEffect } from 'vue'

interface PostPosition {
  top: number
  left: number
}

interface ContentDropOptions {
  /**
   * Optional callback executed *before* showing the default alert
   * when a file is handled while the active draft already has an attachment.
   * If this function is provided and returns `false`, the default alert and
   * replacement logic will be skipped. If it returns `true` or is not provided,
   * the default logic proceeds.
   * @param file The file that was handled.
   * @returns {boolean} Whether to proceed with the default alert/logic.
   */
  onFileDropWithExistingAttachment?: (file: File) => boolean
  /**
   * The source of the content operation - either 'drop' (drag and drop) or 'paste'.
   * This is used to customize alert messages for user clarity.
   * Defaults to 'drop' for backwards compatibility.
   */
  contentSource?: 'drop' | 'paste'
}

/**
 * Common content handling logic that can be used by both drop and paste operations.
 * This composable is shared between useDropToCreatePost and usePasteToCreatePost.
 */
export const useContentDrop = ({ onFileDropWithExistingAttachment, contentSource = 'drop' }: ContentDropOptions = {}): {
  handleFilesDrop: typeof handleFilesDrop
  handleLinkDrop: typeof handleLinkDrop
  handleTextDrop: typeof handleTextDrop
  handleSingleFileIfActiveDraftIsPresent: typeof handleSingleFileIfActiveDraftIsPresent
  handleMultipleFileIfActiveDraftIsPresent: typeof handleMultipleFileIfActiveDraftIsPresent
  handleSingleFileIfNoActiveDraft: typeof handleSingleFileIfNoActiveDraft
  handleMultipleFileIfNoActiveDraft: typeof handleMultipleFileIfNoActiveDraft
} => {
  const { openComposerAlert } = useComposerModalAlertStore()
  const surfacePostsStore = useSurfacePostsStore()
  const { createPostImmediately } = surfacePostsStore

  const surfaceDraftsStore = useSurfaceDraftsStore()
  const {
    startNewDraft,
    updateActiveDraft,
    uploadFileActiveDraft,
    removeDraftAttachment,
    replaceActiveDraftUploadingFile,
    replaceActiveDraftAttachmentWithLink,
  } = surfaceDraftsStore
  const { activeDraft, activeDraftUploadingFile, showPublishAllDraftsPopover, isAnyDraftActive } =
    storeToRefs(surfaceDraftsStore)
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const surfaceGroupPickerStore = useSurfaceGroupPickerStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfacePanelsStore = useSurfacePanelsStore()
  const { isContainerSmallerThanTabletPortrait } = storeToRefs(useSurfaceContainerSizeStore())

  const { onUploadStart, selectFiles } = usePhotoAlbumEditor()

  const activeDraftHasAttachmentOrUploadingFile = computed(
    () => !isEmpty(activeDraft.value?.attachment) || activeDraftUploadingFile.value != null,
  )
  const activeDraftHasPhotoAlbumAttachment = computed(() => {
    return isPhotoAlbum(activeDraft.value)
  })
  const activeDraftHasSingleImageAttachment = computed(() => {
    return (
      !activeDraftHasPhotoAlbumAttachment.value &&
      !isEmpty(activeDraft.value?.attachment) &&
      surfaceAttachmentsStore.getLinkAttributesForUrl(activeDraft.value?.attachment as string)?.content_category ===
        'photo'
    )
  })

  // Auto-fetch link attributes when active draft attachment changes
  watchEffect(() => {
    const attachment = activeDraft.value?.attachment
    if (attachment && !isEmpty(attachment)) {
      const existingLinkAttributes = surfaceAttachmentsStore.getLinkAttributesForUrl(attachment)
      if (existingLinkAttributes == null) {
        void surfaceAttachmentsStore.fetchAndStoreLinkAttributes(attachment)
      }
    }
  })

  /**
   * Shows an alert when a non-image file is dropped into a photo album.
   * If the photo album editor dialog is open, shows a snackbar instead of the composer alert
   * since the dialog would cover the alert.
   */
  const showPhotoAlbumFileTypeAlert = (): void => {
    if (surfacePanelsStore.xPhotoAlbumEditorDialog) {
      globalSnackbarStore.setSnackbar({
        message: __('Only images can be added to the photo album'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    openComposerAlert({
      title: __('Unsupported file type'),
      body: __('Only images can be added to the photo album'),
      primaryActionText: __('Okay'),
      primaryActions: [],
    })
  }

  /**
   * Handles multiple files when an active draft is present.
   * Shows an alert informing the user that only one file can be added at a time.
   * If photo album feature is enabled, allows adding multiple images to an existing photo album.
   * @param files The array of files.
   */
  const handleMultipleFileIfActiveDraftIsPresent = (files: File[]): void => {
    const draft = activeDraft.value as DraftPost

    if (isAppUsing('photoAlbum')) {
      if (activeDraftHasPhotoAlbumAttachment.value) {
        const imageFiles = files.filter((file) => file.type.startsWith('image/'))
        const nonImageFiles = files.filter((file) => !file.type.startsWith('image/'))

        if (nonImageFiles.length > 0) {
          showPhotoAlbumFileTypeAlert()
          return
        }

        const totalAttachments = getAttachmentsFromDraft(draft).length + imageFiles.length
        if (totalAttachments > MAX_PHOTO_ALBUM_ATTACHMENTS) {
          globalSnackbarStore.setSnackbar({
            message: __('No more than %{count} images can be added to a post at a time', {
              count: MAX_PHOTO_ALBUM_ATTACHMENTS,
            }),
            notificationType: SnackbarNotificationType.error,
          })
          return
        }

        selectFiles({ files: imageFiles })
        return
      } else if (activeDraftHasSingleImageAttachment.value) {
        const filesAreAllImages = files.every((file) => file.type.startsWith('image/'))
        if (!filesAreAllImages) {
          showPhotoAlbumFileTypeAlert()
          return
        }
        const numImagesThatCanBeAdded = MAX_PHOTO_ALBUM_ATTACHMENTS - getAttachmentsFromDraft(draft).length
        if (files.length > numImagesThatCanBeAdded) {
          globalSnackbarStore.setSnackbar({
            message: __('No more than %{count} images can be added to a post at a time', {
              count: MAX_PHOTO_ALBUM_ATTACHMENTS,
            }),
            notificationType: SnackbarNotificationType.error,
          })
          return
        }
        selectFiles({ files })
        return
      } else {
        const filesAreAllImages = files.every((file) => file.type.startsWith('image/'))
        if (!filesAreAllImages) {
          openComposerAlert({
            title: contentSource === 'paste' ? __('Multiple files pasted') : __('Multiple files added'),
            body:
              contentSource === 'paste'
                ? __('While writing a post, only one file can be pasted in at a time.')
                : __('While writing a post, only one file can be added at a time'),
            primaryActionText: __('Okay'),
            primaryActions: [],
          })
          return
        }

        if (files.length > MAX_PHOTO_ALBUM_ATTACHMENTS) {
          globalSnackbarStore.setSnackbar({
            message: __('No more than %{count} images can be added to a post at a time', {
              count: MAX_PHOTO_ALBUM_ATTACHMENTS,
            }),
            notificationType: SnackbarNotificationType.error,
          })
          return
        }

        selectFiles({ files })
        return
      }
    }

    openComposerAlert({
      title: contentSource === 'paste' ? __('Multiple files pasted') : __('Multiple files added'),
      body:
        contentSource === 'paste'
          ? __('While writing a post, only one file can be pasted in at a time.')
          : __('While writing a post, only one file can be added at a time'),
      primaryActionText: __('Okay'),
      primaryActions: [],
    })
  }

  /**
   * Handles a single file when an active draft is present.
   *
   * Photo Album handling:
   * - If photo album feature is enabled:
   *   - Checks photo album attachment limit and shows error if exceeded
   *   - For existing photo albums or single image posts:
   *     - If file is an image: adds it to the photo album
   *     - If file is not an image and there's a photo album: shows unsupported file type alert
   *
   * Regular file handling:
   * - If the draft already has an attachment or an upload is in progress,
   *   it prompts the user to confirm replacement (respecting the optional callback).
   * - Otherwise, it uploads the file to the active draft.
   *
   * @param fileToUpload The single file.
   */
  const handleSingleFileIfActiveDraftIsPresent = (fileToUpload: File): void => {
    const draft = activeDraft.value as DraftPost
    if (isAppUsing('photoAlbum')) {
      if (
        activeDraftHasPhotoAlbumAttachment.value &&
        getAttachmentsFromDraft(draft).length === MAX_PHOTO_ALBUM_ATTACHMENTS
      ) {
        globalSnackbarStore.setSnackbar({
          message: __('No more than %{count} images can be added to a post at a time', {
            count: MAX_PHOTO_ALBUM_ATTACHMENTS,
          }),
          notificationType: SnackbarNotificationType.error,
        })
        return
      }

      if (
        (activeDraftHasSingleImageAttachment.value || activeDraftHasPhotoAlbumAttachment.value) &&
        fileToUpload.type.startsWith('image/')
      ) {
        onUploadStart({ file: fileToUpload })
        return
      } else if (activeDraftHasPhotoAlbumAttachment.value && !fileToUpload.type.startsWith('image/')) {
        showPhotoAlbumFileTypeAlert()
        return
      }
    }

    // Ask if user wants to replace the current attachment.
    if (activeDraftHasAttachmentOrUploadingFile.value) {
      // Check if a custom handler is provided and if it signals to stop
      let proceed = true
      if (onFileDropWithExistingAttachment != null) {
        proceed = onFileDropWithExistingAttachment(fileToUpload)
      }
      if (!proceed) return

      // Only proceed with the default alert if the callback didn't return false
      const primaryActions =
        activeDraft.value?.attachment != null
          ? [
              // An attachment already exists
              // -> remove and upload the new one.
              () => {
                if (activeDraft.value?.cid != null) removeDraftAttachment(activeDraft.value.cid)
              },
              () => uploadFileActiveDraft({ file: fileToUpload }),
            ]
          : [
              // An attachment is being uploaded,
              // when the user click "Replace", it can either be still uploading or already uploaded and added to activeDraft.attachment.
              () => replaceActiveDraftUploadingFile({ file: fileToUpload }),
            ]
      openComposerAlert({
        title: __('An attachment already exists'),
        body: __('Are you sure you want to replace your current attachment?'),
        primaryActionText: __('Replace'),
        secondaryActionText: __('Cancel'),
        secondaryActions: [],
        primaryActions,
      })
      return
    }

    uploadFileActiveDraft({ file: fileToUpload })
  }

  /**
   * Handles a single file when no draft is active.
   * Creates a new draft with the file as an attachment at the specified position.
   * @param file The single file.
   * @param position The position where the file was placed (only used for drop operations).
   */
  const handleSingleFileIfNoActiveDraft = (file: File, position: PostPosition = { top: 0, left: 0 }): void => {
    const attributes = { file, ...position }
    const activeDraftCid = activeDraft.value?.cid
    const shouldStartEditing = activeDraftCid == null
    startNewDraft({ attributes, shouldStartEditing })
  }

  /**
   * Handles multiple file drops on mobile viewports where minimized drafts are not visible.
   *
   * Creates the first file as a draft and opens it immediately, then creates remaining files
   * as minimized drafts in the background. Shows snackbar notification about drafts being created.
   *
   * This ensures mobile users get immediate visual feedback with the composer opening
   * for the first file, while remaining files are processed in the background.
   *
   * @param files The array of files to handle
   */
  const handleMultipleFileOnPhoneIfNoActiveDraft = (
    files: File[],
    postAttributes: Pick<PostAttributes, 'top' | 'left' | 'wall_section_id' | 'color' | 'custom_properties'>,
  ): void => {
    // Create the first draft and open it
    startNewDraft({
      attributes: {
        file: files[0],
        ...postAttributes,
      },
      shouldStartEditing: true,
    })

    // Create other drafts in the background
    const remainingFiles = files.slice(1)
    remainingFiles.forEach((file, index) => {
      startNewDraft({
        attributes: {
          file,
          ...postAttributes,
          top: (postAttributes.top ?? 0) + (index + 1) * 16,
          left: (postAttributes.left ?? 0) + (index + 1) * 16,
        },
        shouldStartEditing: false,
      })
    })

    globalSnackbarStore.setSnackbar({
      message: __('%{count} drafts will be created', { count: files.length }),
      notificationType: SnackbarNotificationType.success,
    })
  }

  /**
   * Handles multiple files when no draft is active.
   * On desktop/tablet viewports, creates minimized posts immediately for each file, slightly offset from each other,
   * and shows the "Publish All Drafts" popover.
   * On mobile portrait viewports, delegates to handleMultipleFileOnPhoneIfNoActiveDraft for special handling.
   * NOTE: Mobile landscape viewport is not supported because post editor is not designed to look good on this orientation yet
   * @param files The array of files.
   * @param position The position where the files were placed (only used for drop operations).
   */
  const handleMultipleFileIfNoActiveDraft = async (
    files: File[],
    position: PostPosition = { top: 0, left: 0 },
  ): Promise<void> => {
    // Check if grouping is required
    const requiresGrouping =
      surfaceWishArrangementStore.isGroupBySection ||
      surfaceWishArrangementStore.isGroupByColor ||
      surfaceWishArrangementStore.isGroupBySingleSelect

    let postAttributes: PostAttributes = {}

    if (requiresGrouping) {
      // Show group picker and wait for selection
      const result = await surfaceGroupPickerStore.show()
      if (!result) return

      const { groupId } = result

      postAttributes = getPostAttributesForGroup(groupId)
    }

    // Create posts with the group attributes already set
    if (isContainerSmallerThanTabletPortrait.value) {
      handleMultipleFileOnPhoneIfNoActiveDraft(files, { ...position, ...postAttributes })
    } else {
      files.forEach((file, index) => {
        startNewDraft({
          attributes: {
            file,
            top: position.top + index * 16,
            left: position.left + index * 16,
            ...postAttributes,
          },
          shouldStartEditing: false,
        })
      })
      showPublishAllDraftsPopover.value = true
    }
  }

  /**
   * Converts a group ID into the appropriate post attributes based on the current grouping type.
   *
   * @param groupId The selected group ID (section ID, color ID, or property value)
   * @returns Post attributes object with the appropriate field set:
   *          - `wall_section_id` for section grouping
   *          - `color` for color grouping
   *          - `custom_properties` for single-select property grouping
   */
  function getPostAttributesForGroup(
    groupId: WallGroupPost['id'],
  ): Pick<PostAttributes, 'wall_section_id' | 'color' | 'custom_properties'> {
    // Section grouping: assign to a specific section
    if (surfaceWishArrangementStore.isGroupBySection && typeof groupId === 'number') {
      return { wall_section_id: groupId }
    }
    // Color grouping: assign a color (or null for default)
    else if (surfaceWishArrangementStore.isGroupByColor && isPostColorId(groupId)) {
      return { color: groupId === 'default' ? null : groupId }
    }
    // Single-select property grouping: set custom property value
    else if (
      surfaceWishArrangementStore.isGroupBySingleSelect &&
      surfaceWishArrangementStore.activeSingleSelectGroupByProperty &&
      typeof groupId === 'string'
    ) {
      const { custom_properties } = setCustomPropertyValue(
        {},
        surfaceWishArrangementStore.activeSingleSelectGroupByProperty,
        groupId === WallWishGroupByValue.NoValue ? null : groupId,
      )
      return { custom_properties }
    }
    return {}
  }

  /**
   * Main handler for file operations.
   *
   * Behavior:
   * - If there's no active draft:
   *   - Single file: Creates a new draft with the file as an attachment
   *   - Multiple files: Creates individual minimized posts for each file
   * - If there's an active draft and files are dropped inside the composer:
   *   - Single file: Adds to or replaces attachment in the active draft
   *   - Multiple files: Adds to photo album or shows alert based on context
   *
   * @param files The array of files to handle.
   * @param position The position where the files were placed (only used for drop operations).
   */
  const handleFilesDrop = async ({
    files,
    position = { top: 0, left: 0 },
  }: {
    files: File[]
    position?: PostPosition
  }): Promise<void> => {
    if (files.length === 0) return
    if (!surfacePostPropertiesStore.shouldHandleFileUpload) {
      openComposerAlert({
        title: __('This attachment type is not allowed'),
        body: __('File uploads are not enabled for this padlet'),
        primaryActionText: __('Okay'),
        primaryActions: [],
      })
      return
    }

    // If there's no active draft, create a new draft with the dropped files.
    if (activeDraft.value == null) {
      // If there's no active draft, create a new draft with the dropped files.
      if (files.length === 1) {
        handleSingleFileIfNoActiveDraft(files[0], position)
      } else {
        await handleMultipleFileIfNoActiveDraft(files, position)
      }
      return
    }

    // If the active draft has an attachment, fetch the link attributes.
    const attachment = activeDraft.value?.attachment
    if (attachment && surfaceAttachmentsStore.getLinkAttributesForUrl(attachment) == null) {
      await surfaceAttachmentsStore.fetchAndStoreLinkAttributes(attachment)
    }

    // If there's an active draft, handle the files accordingly.
    if (files.length === 1) {
      handleSingleFileIfActiveDraftIsPresent(files[0])
    } else {
      handleMultipleFileIfActiveDraftIsPresent(files)
    }
  }

  const handleLinkDrop = ({
    urls,
    position = { top: 0, left: 0 },
  }: {
    urls: string[]
    position?: PostPosition
  }): void => {
    if (urls.length === 0) return
    if (!surfacePostPropertiesStore.shouldHandleLinks) {
      openComposerAlert({
        title: __('This attachment type is not allowed'),
        body: __('Link attachments are not enabled for this padlet'),
        primaryActionText: __('Okay'),
        primaryActions: [],
      })
      return
    }

    if (isAnyDraftActive.value) {
      // For links, we conveniently pick the first one to add as an attachment to the active draft.
      const link = urls[0]

      // Ask if user wants to replace the current attachment.
      if (activeDraftHasAttachmentOrUploadingFile.value) {
        openComposerAlert({
          title: __('An attachment already exists'),
          body: __('Are you sure you want to replace your current attachment?'),
          primaryActionText: __('Replace'),
          secondaryActionText: __('Cancel'),
          secondaryActions: [],
          primaryActions: [() => replaceActiveDraftAttachmentWithLink({ link })],
        })
        return
      }

      updateActiveDraft({
        attachment: link,
        wish_content: {
          attachment_props: {
            url: link,
          },
          is_processed: false,
        },
      })
      return
    }

    if (urls.length === 1) {
      // There's only 1 link, create a new draft and add it inside.
      const attachment = urls[0]
      const attributes = { attachment, ...position }
      const activeDraftCid = activeDraft.value?.cid
      const shouldStartEditing = activeDraftCid == null
      startNewDraft({ attributes, shouldStartEditing })
    } else {
      // Add the links in minimized drafts.
      urls.forEach((attachment, index) => {
        const newPost = {
          attributes: {
            attachment,
            // Place each post a few pixels away from each other.
            top: position.top + index * 16,
            left: position.left + index * 16,
          },
        }
        createPostImmediately(newPost)
      })
    }
  }

  const handleTextDrop = ({
    text,
    position = { top: 0, left: 0 },
  }: {
    text: string | null
    position?: PostPosition
  }): void => {
    if (text == null) return

    if (isAnyDraftActive.value) {
      // Append the text to the draft body.
      updateActiveDraft({
        body: (activeDraft.value?.body ?? '') + '<br/>' + text,
      })
      return
    }

    const attributes = { body: text, ...position }
    const activeDraftCid = activeDraft.value?.cid
    const shouldStartEditing = activeDraftCid == null
    startNewDraft({ attributes, shouldStartEditing })
  }

  return {
    handleFilesDrop,
    handleLinkDrop,
    handleTextDrop,
    handleSingleFileIfActiveDraftIsPresent,
    handleMultipleFileIfActiveDraftIsPresent,
    handleSingleFileIfNoActiveDraft,
    handleMultipleFileIfNoActiveDraft,
  }
}
